import { ref, type Ref } from "vue";
import type { AnchorRect } from "./useAnchoredFloating";
import type { EditorSelectionSnapshot } from "../editor/port";
import {
  displayModeAllowsComments,
  displayModeIsReading,
  type DisplayMode,
} from "../lib/displayMode";
import { isJust, isNothing, just, nothing, type Maybe } from "../lib/maybe";

/** The mobile-only floating format toolbar that appears over a text
 * selection (desktop uses the persistent FormatRibbon instead). */
export function useFormatBubble(options: {
  /** The editor's current selection (Nothing when no editor is mounted). */
  getSelection: () => Maybe<EditorSelectionSnapshot>;
  /** Collapse a range selection (dismissing the bubble). */
  collapseSelection: () => void;
  shell: Ref<Maybe<HTMLElement>>;
  mobileShell: Ref<boolean>;
  displayMode: Ref<DisplayMode>;
  reviewModeActive: Ref<boolean>;
  keyboardInsetPx: Ref<number>;
  /** Hidden while a comment card is open. */
  openId: Ref<Maybe<string>>;
  /** Hidden while any image UI is open. */
  imageToolbarVisible: Ref<boolean>;
  altDialogOpen: Ref<boolean>;
  cropDialogOpen: Ref<boolean>;
}): {
  bubbleAnchor: Ref<
    | { readonly tag: "nothing" }
    | {
        readonly tag: "just";
        readonly value: {
          top: number;
          left: number;
          width: number;
          height: number;
        };
      },
    | { readonly tag: "nothing" }
    | Maybe<AnchorRect>
    | {
        readonly tag: "just";
        readonly value: {
          top: number;
          left: number;
          width: number;
          height: number;
        };
      }
  >;
  bubbleVisible: Ref<boolean, boolean>;
  bubblePinned: Ref<boolean, boolean>;
  /** Re-read the editor's selection and place / hide the bubble. */
  syncFormatBubble: () => void;
  syncFormatBubbleFromSelection: (
    selection: Maybe<EditorSelectionSnapshot>,
  ) => void;
  onBubblePin: (pinned: boolean) => void;
  onBubbleDismiss: () => void;
} {
  const bubbleAnchor = ref<Maybe<AnchorRect>>(nothing);
  const bubbleVisible = ref(false);
  const bubblePinned = ref(false);

  function syncFormatBubble(): void {
    syncFormatBubbleFromSelection(options.getSelection());
  }

  function syncFormatBubbleFromSelection(
    selectionM: Maybe<EditorSelectionSnapshot>,
  ): void {
    if (isNothing(selectionM) || isNothing(options.shell.value)) {
      if (bubblePinned.value === false) bubbleVisible.value = false;
      return;
    }
    const sel = selectionM.value;
    const shell = options.shell.value.value;
    const mode = options.displayMode.value;
    // Desktop uses the global FormatRibbon — except Reading, which hides the
    // ribbon and needs a selection-only Add comment control when allowed.
    if (
      options.mobileShell.value === false &&
      displayModeIsReading(mode) === false
    ) {
      bubbleVisible.value = false;
      return;
    }
    if (mode === "reading" || mode === "focus") {
      bubbleVisible.value = false;
      return;
    }
    // Hide while a comment card or image UI is open.
    if (
      isJust(options.openId.value) ||
      options.imageToolbarVisible.value === true ||
      options.altDialogOpen.value === true ||
      options.cropDialogOpen.value === true
    ) {
      bubbleVisible.value = false;
      return;
    }

    // Touch / Reading: only show for a real selection (not a caret).
    if (sel.empty === true) {
      bubbleVisible.value = false;
      return;
    }

    // Focus left the editor (file dialog, another panel, etc.) — never stay
    // stuck. Reading-with-comments still shows Add comment for a live
    // selection even when contenteditable briefly lacks focus.
    if (
      sel.hasFocus === false &&
      (displayModeIsReading(mode) === false ||
        displayModeAllowsComments(mode) === false)
    ) {
      bubblePinned.value = false;
      bubbleVisible.value = false;
      return;
    }

    const anchor = Math.min(sel.from, sel.to);
    const coords = sel.coordsAtPos(anchor);
    if (coords === null) {
      bubbleVisible.value = false;
      return;
    }

    const shellRect = shell.getBoundingClientRect();

    // Reading on desktop: float above the selection (ribbon is hidden).
    if (
      displayModeIsReading(mode) === true &&
      options.mobileShell.value === false
    ) {
      const end = Math.max(sel.from, sel.to);
      const endCoords = sel.coordsAtPos(end) ?? coords;
      bubbleAnchor.value = just({
        top: coords.top,
        left: coords.left,
        width: Math.max(endCoords.right - coords.left, 1),
        height: Math.max(coords.bottom, endCoords.bottom) - coords.top,
      });
      bubbleVisible.value = true;
      return;
    }

    // Dock across the bottom of the shell rather than tracking the selection, so
    // Android's native selection handles don't cover the controls. The shell
    // keeps its full height when the keyboard opens, so subtract the keyboard
    // along with the status dock.
    const dockClearance = 52;
    bubbleAnchor.value = just({
      top: shellRect.bottom - dockClearance - options.keyboardInsetPx.value,
      left: shellRect.left,
      width: shellRect.width,
      height: 0,
    });
    bubbleVisible.value = true;
  }

  function onBubblePin(pinned: boolean): void {
    bubblePinned.value = pinned;
    // If the bubble is hidden (e.g. image picker opened), ignore stale leave events.
    if (pinned === false && bubbleVisible.value === true) syncFormatBubble();
  }

  function onBubbleDismiss(): void {
    bubblePinned.value = false;
    bubbleVisible.value = false;
    // Collapse the selection so syncFormatBubble stays hidden (mobile only
    // shows the dock for a non-empty range).
    options.collapseSelection();
  }

  return {
    bubbleAnchor,
    bubbleVisible,
    bubblePinned,
    syncFormatBubble,
    syncFormatBubbleFromSelection,
    onBubblePin,
    onBubbleDismiss,
  };
}
